/**
 * LAGOSClimateCircularity — /state/lagos/climate
 * Analytical decision-support workspace.
 *
 * Enhancements:
 *   A. Neutral/slate for baseline, green for reduction (no red misread)
 *   B. Waterfall bridge: Baseline → Reduction → Net Residual
 *   C. Intensity layer: tCO₂e per entity, reduction per entity
 *   D. Automated insight panel (system-generated, dynamic)
 *   E. Dedicated circularity quadrant / sector ranking
 *   F. All values SSOT-driven from useLagos() portfolio
 */
import React, { useState, useMemo } from "react";
import { useLagos } from "@/context/LAGOSContext";
import { SectionCard } from "@/components/ds";
import { Leaf, TrendingDown, Recycle, BarChart2, Lightbulb, Activity } from "lucide-react";

const fmtN = (v, u = "") => {
  if (!v && v !== 0) return "—";
  const n = Number(v);
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M ${u}`.trim();
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K ${u}`.trim();
  return `${n.toLocaleString()} ${u}`.trim();
};

const fmtPct = v => `${Number(v || 0).toFixed(1)}%`;

// ── BarRow: neutral slate for baseline, green for reduction ──────────────────
const BarRow = ({ label, value, max, color = "#64748b", sub, count, suffix = "" }) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="space-y-1 py-2 border-b border-slate-50 last:border-0">
      <div className="flex justify-between items-baseline text-xs">
        <span className="font-medium text-slate-700 capitalize max-w-[170px] truncate">
          {label.replace(/_/g, " ")}
        </span>
        <div className="flex items-center gap-2 flex-shrink-0">
          {count !== undefined && <span className="text-[10px] text-slate-400">{count} entities</span>}
          <span className="font-black text-xs" style={{ color }}>
            {fmtN(value)}{suffix}
          </span>
        </div>
      </div>
      <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: pct + "%", background: color }} />
      </div>
      {sub && <p className="text-[9px] text-slate-400">{sub}</p>}
    </div>
  );
};

// ── Waterfall Bridge (Baseline → Reduction → Net) ────────────────────────────
function WaterfallBridge({ baseline, reduction, net }) {
  const total = baseline;
  if (!total) return null;
  const redPct = Math.min(100, (reduction / total) * 100);
  const netPct = Math.min(100, (net / total) * 100);
  return (
    <div className="space-y-3">
      {/* Visual bridge bar */}
      <div className="relative h-10 bg-slate-100 rounded-xl overflow-hidden flex">
        {/* Reduction segment */}
        <div
          className="h-full flex items-center justify-center text-[9px] font-bold text-white transition-all"
          style={{ width: redPct + "%", background: "#059669" }}
          title={`Reduction potential: ${fmtN(reduction)} tCO₂e/yr`}
        >
          {redPct > 8 ? `${redPct.toFixed(0)}%` : ""}
        </div>
        {/* Residual/net segment */}
        <div
          className="h-full flex items-center justify-center text-[9px] font-bold text-slate-500 transition-all"
          style={{ width: netPct + "%", background: "#cbd5e1" }}
          title={`Net residual: ${fmtN(net)} tCO₂e/yr`}
        >
          {netPct > 8 ? `${netPct.toFixed(0)}%` : ""}
        </div>
      </div>

      {/* Labels */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Baseline Emissions",    value: fmtN(baseline, "tCO₂e/yr"), color: "#0f172a", sub: "Total SSOT baseline" },
          { label: "Reduction Potential",   value: fmtN(reduction, "tCO₂e/yr"), color: "#059669", sub: fmtPct((reduction/Math.max(baseline,1))*100) + " achievable" },
          { label: "Net Residual",          value: fmtN(net, "tCO₂e/yr"),      color: "#64748b", sub: fmtPct((net/Math.max(baseline,1))*100) + " of baseline" },
        ].map(({ label, value, color, sub }) => (
          <div key={label} className="text-center">
            <p className="text-[9px] text-slate-400 uppercase tracking-wider">{label}</p>
            <p className="text-lg font-black mt-0.5" style={{ color }}>{value}</p>
            <p className="text-[9px] text-slate-400">{sub}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Circularity Quadrant (2×2 grid: high/low climate × high/low circularity) ──
function CircularityQuadrant({ sectors }) {
  // Climate score avg and circularity avg as benchmark midpoints
  const avgClimate   = sectors.length > 0 ? sectors.reduce((s, x) => s + x.clim, 0) / sectors.length : 50;
  const avgCirc      = sectors.length > 0 ? sectors.reduce((s, x) => s + x.circ, 0) / sectors.length : 50;

  const quadrant = (clim, circ) => {
    if (clim >= avgClimate && circ >= avgCirc) return { q: "Leaders",     bg: "#ecfdf5", border: "#6ee7b7", text: "#065f46" };
    if (clim >= avgClimate && circ <  avgCirc) return { q: "Mitigators",  bg: "#eff6ff", border: "#93c5fd", text: "#1e40af" };
    if (clim <  avgClimate && circ >= avgCirc) return { q: "Circulators", bg: "#fef9c3", border: "#fde047", text: "#854d0e" };
    return                                           { q: "Priority TA",  bg: "#fef2f2", border: "#fca5a5", text: "#991b1b" };
  };

  return (
    <div className="space-y-3">
      {/* Legend */}
      <div className="flex flex-wrap gap-2 text-[9px]">
        {[
          { q: "Leaders",     bg: "#ecfdf5", text: "#065f46", desc: "High climate + high circularity" },
          { q: "Mitigators",  bg: "#eff6ff", text: "#1e40af", desc: "High climate + lower circularity" },
          { q: "Circulators", bg: "#fef9c3", text: "#854d0e", desc: "Lower climate + high circularity" },
          { q: "Priority TA", bg: "#fef2f2", text: "#991b1b", desc: "Both below avg — needs intervention" },
        ].map(({ q, bg, text, desc }) => (
          <div key={q} className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-sm" style={{ background: bg, border: `1px solid ${text}` }} />
            <span style={{ color: text }} className="font-bold">{q}</span>
            <span className="text-slate-400">— {desc}</span>
          </div>
        ))}
      </div>

      {/* Sector dots */}
      <div className="space-y-1">
        {sectors.map(s => {
          const q = quadrant(s.clim, s.circ);
          return (
            <div key={s.sector} className="flex items-center gap-3 rounded-lg px-3 py-2"
              style={{ background: q.bg, border: `1px solid ${q.border}` }}>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium capitalize truncate" style={{ color: q.text }}>
                  {s.sector.replace(/_/g, " ")}
                </p>
                <p className="text-[9px] text-slate-400">{s.count} entities · {fmtN(s.tco2e, "tCO₂e")}</p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="text-center">
                  <p className="text-[8px] text-slate-400">Climate</p>
                  <p className="text-xs font-black" style={{ color: q.text }}>{s.clim.toFixed(0)}</p>
                </div>
                <div className="text-center">
                  <p className="text-[8px] text-slate-400">Circularity</p>
                  <p className="text-xs font-black" style={{ color: q.text }}>{s.circ.toFixed(0)}</p>
                </div>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: q.bg, color: q.text }}>
                  {q.q}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Automated Insight Panel ───────────────────────────────────────────────────
function InsightPanel({ portfolio }) {
  const byS = portfolio.by_sector || {};
  const byL = portfolio.by_lga    || {};

  // Derive dynamic insights from SSOT data
  const insights = useMemo(() => {
    const sectorList = Object.entries(byS).map(([s, d]) => ({ sector: s, ...d }));
    const lgaList    = Object.entries(byL).map(([l, d]) => ({ lga: l, ...d }));

    const topEmitter   = sectorList.sort((a, b) => b.tco2e - a.tco2e)[0];
    const topLeverage  = sectorList.sort((a, b) => (b.reduction / Math.max(b.tco2e, 1)) - (a.reduction / Math.max(a.tco2e, 1)))[0];
    const topCirc      = sectorList.filter(s => s.avg_circ > 0).sort((a, b) => b.avg_circ - a.avg_circ)[0];
    const topLga       = lgaList.sort((a, b) => b.reduction - a.reduction)[0];
    const totalReduction = portfolio.reduction_potential_tco2e || 0;
    const totalBase    = portfolio.total_tco2e_yr || 0;

    return [
      topEmitter && {
        icon: "⬆",
        color: "#64748b",
        text: `Largest emissions driver: ${topEmitter.sector.replace(/_/g," ")} (${fmtN(topEmitter.tco2e, "tCO₂e/yr")}, ${topEmitter.count} entities). Sector accounts for ${totalBase > 0 ? ((topEmitter.tco2e/totalBase)*100).toFixed(0) : 0}% of state baseline.`,
      },
      topLeverage && {
        icon: "⚡",
        color: "#059669",
        text: `Highest reduction leverage: ${topLeverage.sector.replace(/_/g," ")} — ${fmtPct(topLeverage.tco2e > 0 ? (topLeverage.reduction/topLeverage.tco2e)*100 : 0)} reduction rate vs baseline. Interventions here deliver the most tCO₂e per $ invested.`,
      },
      topCirc && {
        icon: "♻",
        color: "#0891b2",
        text: `Highest circularity: ${topCirc.sector.replace(/_/g," ")} (avg ${topCirc.avg_circ.toFixed(0)}/100). High material recovery rate indicates strong circularity credentials and potential for plastic credit revenue.`,
      },
      topLga && {
        icon: "📍",
        color: "#7c3aed",
        text: `Priority intervention LGA: ${topLga.lga} — ${fmtN(topLga.reduction, "tCO₂e/yr")} reduction potential across ${topLga.count} entities. Recommend deploying CPA structuring and evidence upgrade programme here first.`,
      },
      {
        icon: "ℹ",
        color: "#1d4ed8",
        text: `Portfolio reduction potential = ${fmtN(totalReduction, "tCO₂e/yr")} — equivalent to ${totalBase > 0 ? ((totalReduction/(totalBase+totalReduction))*100).toFixed(0) : 0}% of combined baseline+reduction. Carbon value at base $20/t: ${fmtN((portfolio.carbon_value_base_usd || 0), "USD/yr")}.`,
      },
    ].filter(Boolean);
  }, [byS, byL, portfolio]);

  return (
    <div className="bg-[#0f172a] rounded-xl p-5 text-white">
      <div className="flex items-center gap-2 mb-4">
        <Lightbulb size={14} className="text-amber-400" />
        <p className="text-xs font-bold uppercase tracking-wider opacity-60">Automated Insight Panel</p>
        <span className="text-[9px] bg-white/10 px-2 py-0.5 rounded">SSOT-generated · Dynamic</span>
      </div>
      <div className="space-y-3">
        {insights.map((ins, i) => (
          <div key={i} className="flex items-start gap-3 py-2 border-b border-white/10 last:border-0">
            <span className="text-base flex-shrink-0">{ins.icon}</span>
            <p className="text-[11px] text-slate-300 leading-relaxed">{ins.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function LAGOSClimateCircularity() {
  const { portfolio, loading } = useLagos();
  const [activeTab, setActiveTab] = useState("overview");
  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading climate data…</div>;

  const byS = portfolio.by_sector || {};
  const byL = portfolio.by_lga    || {};

  const totalEmissions = portfolio.total_tco2e_yr || 0;
  const totalReduction = portfolio.reduction_potential_tco2e || 0;
  const netResidual    = Math.max(0, totalEmissions - totalReduction);

  // Sorted lists
  const sectorsByEmission  = Object.entries(byS).sort((a, b) => b[1].tco2e    - a[1].tco2e);
  const sectorsByReduction = Object.entries(byS).sort((a, b) => b[1].reduction - a[1].reduction);
  const lgasByEmission     = Object.entries(byL).sort((a, b) => b[1].tco2e    - a[1].tco2e);
  const lgasByReduction    = Object.entries(byL).sort((a, b) => b[1].reduction - a[1].reduction);

  const maxSectorCo2 = sectorsByEmission[0]?.[1]?.tco2e    || 1;
  const maxSectorRed = Math.max(...Object.values(byS).map(d => d.reduction || 0), 1);
  const maxLgaCo2    = lgasByEmission[0]?.[1]?.tco2e        || 1;
  const maxLgaRed    = Math.max(...Object.values(byL).map(d => d.reduction || 0), 1);

  // Intensity data (per entity)
  const sectorIntensity = Object.entries(byS).map(([s, d]) => ({
    sector: s, count: d.count || 1,
    emissionPerEnt:  d.count ? d.tco2e    / d.count : 0,
    reductionPerEnt: d.count ? d.reduction / d.count : 0,
    circ: d.avg_circ || 0,
    tco2e: d.tco2e,
    clim: 0, // will compute below
  })).sort((a, b) => b.emissionPerEnt - a.emissionPerEnt);

  // Circularity quadrant data
  const quadrantData = Object.entries(byS)
    .map(([s, d]) => ({
      sector: s, count: d.count || 1,
      tco2e: d.tco2e || 0,
      circ:  d.avg_circ || 0,
      // Approximate climate score: entities below sector benchmark score higher
      clim:  d.tco2e > 0 ? Math.max(5, 100 - ((d.tco2e / Math.max(d.count, 1)) / 60) * 60) : 75,
    }))
    .sort((a, b) => b.tco2e - a.tco2e);

  const TABS = [
    { id: "overview",    label: "Overview + Bridge",  icon: BarChart2   },
    { id: "emissions",   label: "Emissions",           icon: Leaf        },
    { id: "reduction",   label: "Reduction",           icon: TrendingDown },
    { id: "intensity",   label: "Intensity",           icon: Activity    },
    { id: "circularity", label: "Circularity",         icon: Recycle     },
  ];

  return (
    <div className="p-6 lg:p-8 bg-[#F8FAFC] min-h-screen space-y-5">

      {/* Header */}
      <div>
        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded">
          Lagos State Government
        </span>
        <h1 className="text-2xl font-black text-slate-900 mt-1.5">Climate & Circularity</h1>
        <p className="text-xs text-slate-400 mt-0.5">
          Analytical decision-support workspace · Waterfall bridge · Intensity layer · SSOT-driven
        </p>
      </div>

      {/* Summary Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          ["State GHG Baseline",    fmtN(totalEmissions, "tCO₂e/yr"), "#0f172a", "Gross portfolio emissions"],
          ["Reduction Potential",   fmtN(totalReduction, "tCO₂e/yr"), "#059669", "Achievable with clean interventions"],
          ["Net Residual",          fmtN(netResidual, "tCO₂e/yr"),    "#64748b", "After full reduction programme"],
          ["Avg Circularity",       (portfolio.circularity_score_avg || 0).toFixed(1) + "/100", "#0891b2", "Portfolio average"],
        ].map(([l, v, c, s]) => (
          <div key={l} className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
            <p className="text-[10px] text-slate-400">{l}</p>
            <p className="text-xl font-black mt-0.5" style={{ color: c }}>{v}</p>
            <p className="text-[9px] text-slate-400 mt-0.5">{s}</p>
          </div>
        ))}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1.5 overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium flex-shrink-0 transition-all ${
                activeTab === t.id ? "bg-[#0f172a] text-white" : "text-slate-500 hover:bg-slate-50"
              }`}
              data-testid={`tab-${t.id}`}>
              <Icon size={11} />
              <span className="hidden sm:inline">{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* ── OVERVIEW + BRIDGE tab ──────────────────────────────────────────── */}
      {activeTab === "overview" && (
        <div className="space-y-4">
          <SectionCard title="Baseline → Reduction → Net Residual Bridge">
            <div className="p-5">
              <WaterfallBridge baseline={totalEmissions} reduction={totalReduction} net={netResidual} />
            </div>
          </SectionCard>
          <InsightPanel portfolio={portfolio} />
        </div>
      )}

      {/* ── EMISSIONS tab ─────────────────────────────────────────────────── */}
      {activeTab === "emissions" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SectionCard title="Sector Emissions Baseline — tCO₂e/yr">
            <div className="p-4">
              {sectorsByEmission.map(([s, d]) => (
                <BarRow key={s} label={s} value={d.tco2e} max={maxSectorCo2}
                  color="#64748b" count={d.count}
                  sub={`${fmtPct(totalEmissions > 0 ? (d.tco2e / totalEmissions) * 100 : 0)} of state total`}
                />
              ))}
            </div>
          </SectionCard>
          <SectionCard title="LGA Emissions Baseline — tCO₂e/yr">
            <div className="p-4">
              {lgasByEmission.map(([lga, d]) => (
                <BarRow key={lga} label={lga} value={d.tco2e} max={maxLgaCo2}
                  color="#64748b" count={d.count}
                  sub={`${fmtPct(totalEmissions > 0 ? (d.tco2e / totalEmissions) * 100 : 0)} of state total`}
                />
              ))}
            </div>
          </SectionCard>
        </div>
      )}

      {/* ── REDUCTION tab ─────────────────────────────────────────────────── */}
      {activeTab === "reduction" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SectionCard title="Sector Reduction Potential — tCO₂e/yr">
            <div className="p-4">
              {sectorsByReduction.map(([s, d]) => (
                <BarRow key={s} label={s} value={d.reduction} max={maxSectorRed}
                  color="#059669" count={d.count}
                  sub={`${fmtPct(d.tco2e > 0 ? (d.reduction / d.tco2e) * 100 : 0)} reduction rate vs baseline`}
                />
              ))}
            </div>
          </SectionCard>
          <SectionCard title="LGA Reduction Potential — tCO₂e/yr">
            <div className="p-4">
              {lgasByReduction.map(([lga, d]) => (
                <BarRow key={lga} label={lga} value={d.reduction} max={maxLgaRed}
                  color="#059669" count={d.count}
                  sub={`${fmtPct(totalReduction > 0 ? (d.reduction / totalReduction) * 100 : 0)} of portfolio reduction`}
                />
              ))}
            </div>
          </SectionCard>
        </div>
      )}

      {/* ── INTENSITY tab ─────────────────────────────────────────────────── */}
      {activeTab === "intensity" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SectionCard title="Emissions Intensity — tCO₂e per Entity">
            <div className="p-4">
              {(() => {
                const maxI = Math.max(...sectorIntensity.map(s => s.emissionPerEnt), 1);
                return sectorIntensity.map(s => (
                  <BarRow key={s.sector} label={s.sector}
                    value={Math.round(s.emissionPerEnt)} max={maxI}
                    color="#64748b" count={s.count}
                    sub={`${fmtN(s.tco2e, "tCO₂e total")} ÷ ${s.count} entities`}
                    suffix=" tCO₂e/entity"
                  />
                ));
              })()}
            </div>
          </SectionCard>
          <SectionCard title="Reduction Leverage — tCO₂e per Entity">
            <div className="p-4">
              {(() => {
                const sorted = [...sectorIntensity].sort((a, b) => b.reductionPerEnt - a.reductionPerEnt);
                const maxR   = Math.max(...sorted.map(s => s.reductionPerEnt), 1);
                return sorted.map(s => (
                  <BarRow key={s.sector} label={s.sector}
                    value={Math.round(s.reductionPerEnt)} max={maxR}
                    color="#059669" count={s.count}
                    sub={`Reduction efficiency: ${s.emissionPerEnt > 0 ? ((s.reductionPerEnt / s.emissionPerEnt) * 100).toFixed(0) : 0}% of entity emission`}
                    suffix=" tCO₂e/entity"
                  />
                ));
              })()}
            </div>
          </SectionCard>
        </div>
      )}

      {/* ── CIRCULARITY tab ───────────────────────────────────────────────── */}
      {activeTab === "circularity" && (
        <div className="space-y-4">
          <SectionCard title="Sector Circularity Score — Ranked (avg /100)">
            <div className="p-4">
              {Object.entries(byS)
                .sort((a, b) => (b[1].avg_circ || 0) - (a[1].avg_circ || 0))
                .map(([s, d]) => (
                  <BarRow key={s} label={s}
                    value={Math.round(d.avg_circ || 0)} max={100}
                    color="#0891b2" count={d.count}
                    sub={`${d.count} entities · Composite circularity score`}
                    suffix="/100"
                  />
                ))
              }
            </div>
          </SectionCard>
          <SectionCard title="Climate vs Circularity Quadrant — Sector Positioning">
            <div className="p-4">
              <CircularityQuadrant sectors={quadrantData.slice(0, 12)} />
            </div>
          </SectionCard>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              ["Avg Circularity Score",   (portfolio.circularity_score_avg  || 0).toFixed(1), "#0891b2", "/100"],
              ["Avg Climate Score",       (portfolio.climate_score_avg      || 0).toFixed(1), "#059669", "/100"],
              ["Recycle Credit Yield",    fmtN(portfolio.recycle_credit_tco2e || 0),           "#7c3aed", " tCO₂e/yr"],
            ].map(([k, v, c, u]) => (
              <div key={k} className="bg-white border border-slate-100 rounded-xl p-4">
                <p className="text-[10px] text-slate-400">{k}</p>
                <p className="text-2xl font-black mt-1" style={{ color: c }}>{v}<span className="text-sm opacity-60">{u}</span></p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
